import React, { useState } from 'react';
import { Save, Sparkles, MailCheck } from 'lucide-react';
import { toast } from 'sonner';
import { api, errText } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { VARS } from './campaignUtils';
import { EmailPreview } from './EmailPreview';

const KEYS = ['name', 'event_date', 'registration_link', 'support_whatsapp', 'subject', 'body', 'guide', 'footer'];
const pick = (c) => Object.fromEntries(KEYS.map((k) => [k, c[k] || '']));

function Field({ k, label, form, set, rows, type = 'text', disabled, hint }) {
  const Comp = rows ? Textarea : Input;
  return (
    <div className="space-y-1">
      <Label htmlFor={`msg-${k}`}>{label}</Label>
      <Comp id={`msg-${k}`} type={type} rows={rows} value={form[k]} disabled={disabled}
        onChange={(e) => set({ ...form, [k]: e.target.value })} data-testid={`message-${k}`} />
      {hint && <div className="text-[11px] text-[hsl(var(--muted-foreground))]">{hint}</div>}
    </div>
  );
}

export function MessageEditor({ campaign, canAct, onSaved }) {
  const [form, setForm] = useState(() => pick(campaign));
  const [busy, setBusy] = useState('');
  const dirty = JSON.stringify(form) !== JSON.stringify(pick(campaign));
  const p = { form, set: setForm, disabled: !canAct };

  const run = async (key, fn) => {
    setBusy(key);
    try { await fn(); } catch (e) { toast.error(errText(e)); } finally { setBusy(''); }
  };
  const save = () => run('save', async () => { await api.put(`/campaigns/${campaign.id}`, form); toast.success('Mensaje guardado'); onSaved(); });
  const aiGuide = () => run('ai', async () => {
    const { data } = await api.post(`/campaigns/${campaign.id}/ai-guide`);
    setForm((f) => ({ ...f, guide: data.guide }));
    toast.success('Guía generada con la base de conocimiento. Revísala y guarda.');
  });
  const test = () => run('test', async () => {
    if (dirty) await api.put(`/campaigns/${campaign.id}`, form);
    const { data } = await api.post(`/campaigns/${campaign.id}/test-email`, {});
    toast.success(`Prueba enviada a ${data.to}`);
    onSaved();
  });

  return (
    <div className="grid lg:grid-cols-2 gap-6" data-testid="message-editor">
      <Card className="p-5 space-y-4">
        <div className="grid sm:grid-cols-2 gap-3">
          <Field k="name" label="Nombre del evento ({evento})" {...p} />
          <Field k="event_date" label="Fecha" type="date" {...p} />
          <Field k="registration_link" label="Link de registro ({link_registro})" {...p} />
          <Field k="support_whatsapp" label="WhatsApp de soporte ({whatsapp_soporte})" {...p} />
        </div>
        <Field k="subject" label="Asunto" {...p} />
        <Field k="body" label="Mensaje" rows={12} {...p} hint={<>Campos automáticos: {VARS.map((v) => <code key={v} className="mr-1">{`{${v}}`}</code>)}· **texto** = negrita</>} />
        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <Label htmlFor="msg-guide">Guía paso a paso ({'{guia}'})</Label>
            {canAct && (
              <Button size="sm" variant="outline" onClick={aiGuide} disabled={!!busy} data-testid="message-ai-guide-button">
                <Sparkles className="h-4 w-4 mr-1" />{busy === 'ai' ? 'Generando...' : 'Generar con IA'}
              </Button>
            )}
          </div>
          <Textarea id="msg-guide" rows={9} value={form.guide} disabled={!canAct} onChange={(e) => setForm({ ...form, guide: e.target.value })} data-testid="message-guide" />
        </div>
        <Field k="footer" label="Pie del correo" rows={4} {...p} hint="Indica que no se responde a este correo y que las dudas van por WhatsApp de soporte." />
        {canAct && (
          <div className="flex flex-wrap gap-2 justify-end">
            <Button variant="outline" onClick={test} disabled={!!busy} data-testid="message-test-button">
              <MailCheck className="h-4 w-4 mr-1" />{busy === 'test' ? 'Enviando...' : 'Enviarme una prueba'}
            </Button>
            <Button onClick={save} disabled={!!busy || !dirty} data-testid="message-save-button">
              <Save className="h-4 w-4 mr-1" />{busy === 'save' ? 'Guardando...' : 'Guardar'}
            </Button>
          </div>
        )}
      </Card>
      <EmailPreview campaignId={campaign.id} draft={form} />
    </div>
  );
}
